import React, { useState, useEffect, useMemo, useRef } from "react";
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  listCourses,
  listMyCourses,
  getCourseDetails,
  getCourseChatHistory,
  sendMessageToAiTutor,
  markChapterProgress,
  getChapterPlayback,
  getQuizDrafts,
  clearQuizDraft,
} from '../../rtk/thunks/courseThunk';
import { getSubscriptionStatus } from '../../rtk/thunks/paymentsThunk';
import useResponsive from '../../hooks/useResponsive';

// --- SVG ICON COMPONENTS ---
const QuickQuizIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);
const AiIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.99964 16.98C3.99964 17.7756 4.31572 18.5387 4.87832 19.1013C5.44093 19.6639 6.204 19.98 6.99964 19.98C6.99964 20.643 7.26304 21.2789 7.73188 21.7477C8.20072 22.2166 8.8366 22.48 9.49964 22.48C10.1627 22.48 10.7986 22.2166 11.2674 21.7477C11.7363 21.2789 11.9996 20.643 11.9996 19.98C11.9996 20.643 12.263 21.2789 12.7319 21.7477C13.2007 22.2166 13.8366 22.48 14.4996 22.48C15.1627 22.48 15.7986 22.2166 16.2674 21.7477C16.7363 21.2789 16.9996 20.643 16.9996 19.98C17.5315 19.98 18.0538 19.8387 18.513 19.5704C18.9723 19.3022 19.352 18.9166 19.6132 18.4534C19.8744 17.9901 20.0077 17.4657 19.9995 16.9339C19.9914 16.4021 19.842 15.882 19.5666 15.427C20.2511 15.2947 20.8682 14.9283 21.3119 14.3906C21.7556 13.853 21.9983 13.1776 21.9983 12.4805C21.9983 11.7834 21.7556 11.108 21.3119 10.5703C20.8682 10.0327 20.2511 9.66623 19.5666 9.53398C19.8422 9.07892 19.9918 8.55875 20.0001 8.02682C20.0084 7.49488 19.8751 6.9703 19.6139 6.50685C19.3527 6.04341 18.9729 5.65776 18.5135 5.38946C18.0541 5.12115 17.5316 4.97983 16.9996 4.97998C16.9996 4.31694 16.7363 3.68105 16.2674 3.21221C15.7986 2.74337 15.1627 2.47998 14.4996 2.47998C13.8366 2.47998 13.2007 2.74337 12.7319 3.21221C12.263 3.68105 11.9996 4.31694 11.9996 4.97998C11.9996 4.31694 11.7363 3.68105 11.2674 3.21221C10.7986 2.74337 10.1627 2.47998 9.49964 2.47998C8.8366 2.47998 8.20072 2.74337 7.73188 3.21221C7.26304 3.68105 6.99964 4.31694 6.99964 4.97998C6.46779 4.97994 5.94549 5.12128 5.48625 5.38954C5.027 5.6578 4.64733 6.04332 4.38612 6.50661C4.12492 6.9699 3.99157 7.4943 3.99975 8.02609C4.00792 8.55788 4.15732 9.07794 4.43264 9.53298C3.74818 9.66523 3.13112 10.0317 2.6874 10.5693C2.24367 11.107 2.00098 11.7824 2.00098 12.4795C2.00098 13.1766 2.24367 13.852 2.6874 14.3896C3.13112 14.9273 3.74818 15.2937 4.43264 15.426C4.14913 15.8947 3.99939 16.4322 3.99964 16.98Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.5 14.98L9.342 9.45398C9.38824 9.31608 9.47664 9.19618 9.59471 9.11124C9.71278 9.0263 9.85455 8.9806 10 8.9806C10.1455 8.9806 10.2872 9.0263 10.4053 9.11124C10.5234 9.19618 10.6118 9.31608 10.658 9.45398L12.5 14.98M15.5 8.97998V14.98M8.5 12.98H11.5"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const CustomCheckbox = ({ completed, onClick, disabled }) => {
  const styles = {
    checkboxContainer: {
      width: "20px",
      height: "20px",
      borderRadius: "50%",
      border: `1.5px solid ${disabled ? "#b8c0cc" : completed ? "#0A2540" : "#dcdcdc"}`,
      backgroundColor: disabled ? "#eef1f5" : completed ? "#0A2540" : "transparent",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: 0,
      flexShrink: 0,
      cursor: disabled ? "not-allowed" : "pointer",
    },
    checkmark: { color: "white", fontSize: "14px", fontWeight: "bold" },
    lockmark: { color: "#6b7280", display: "inline-flex" },
  };
  return (
    <div style={styles.checkboxContainer} onClick={onClick}>
      {disabled ? (
        <span style={styles.lockmark}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2.4" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          </svg>
        </span>
      ) : completed && <span style={styles.checkmark}>&#10003;</span>}
    </div>
  );
};

const SubscriptionLockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ProgressLockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M8 10V8a4 4 0 0 1 6.8-2.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M16 4l3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const isVideoWatched = (chapter) => Boolean(chapter?.is_completed || chapter?.completed);

const chapterHasQuiz = (chapter) => chapter?.has_quiz === true;

const isChapterCompleted = (chapter) => {
  // Checkbox / progress: video watched, and quiz attempted (pass or fail) when the chapter has one.
  if (!chapterHasQuiz(chapter)) {
    return isVideoWatched(chapter);
  }
  return Boolean(chapter?.quiz_attempted) && isVideoWatched(chapter);
};

/** True when this chapter no longer blocks unlocking the next new chapter. */
const isChapterProgressDone = (chapter) => {
  if (!chapterHasQuiz(chapter)) {
    return isVideoWatched(chapter);
  }
  // Unlock next after video watched AND quiz attempted (pass or fail).
  return Boolean(chapter?.quiz_attempted) && isVideoWatched(chapter);
};

const isFreePreviewChapter = (domain, chapter) => {
  const domainOrder = Number(domain?.order);
  const chapterOrder = Number(chapter?.order);
  if (domainOrder === 0) return true;
  return chapterOrder === 1;
};

const getOrderedChapters = (domains) =>
  [...(domains || [])]
    .sort((left, right) => (left.order || 0) - (right.order || 0))
    .flatMap((domain) =>
      [...(domain.chapters || [])].sort((left, right) => (left.order || 0) - (right.order || 0))
    );

const buildChapterAccessMap = (domains, hasActiveSubscription) => {
  const accessMap = new Map();
  const orderedDomains = [...(domains || [])].sort(
    (left, right) => (left.order || 0) - (right.order || 0)
  );
  let previousQuizSatisfied = true;

  orderedDomains.forEach((domain) => {
    const chapters = [...(domain.chapters || [])].sort(
      (left, right) => (left.order || 0) - (right.order || 0)
    );
    chapters.forEach((chapter) => {
      const isFree = isFreePreviewChapter(domain, chapter);

      // Already-attempted chapters stay open for retake (even if earlier ones failed).
      const alreadyAttempted = Boolean(chapter?.quiz_attempted);

      let lockType = null;
      if (isFree) {
        lockType = null;
      } else if (!hasActiveSubscription) {
        lockType = "subscription";
      } else if (!previousQuizSatisfied && !alreadyAttempted) {
        lockType = "progress";
      }

      accessMap.set(String(chapter.id), {
        isLocked: Boolean(lockType),
        lockType,
      });

      // Next new chapter stays gated until this one is fully done (video + pass if quiz).
      previousQuizSatisfied = previousQuizSatisfied && isChapterProgressDone(chapter);
    });
  });

  return accessMap;
};

/** Resume at the first unlocked chapter not yet passed (or furthest attempted weak area). */
const getProgressChapter = (chapters, chapterAccessById) => {
  const unlocked = (chapters || []).filter(
    (chapter) => !chapterAccessById.get(String(chapter.id))?.isLocked
  );
  // Prefer first not-passed among unlocked so sequential progress is clear.
  const nextIncomplete = unlocked.find((chapter) => !isChapterProgressDone(chapter));
  return nextIncomplete || unlocked[unlocked.length - 1] || chapters?.[0] || null;
};

// --- CHILD COMPONENTS (Now aware of mobile state) ---

const Header = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(listCourses());
    dispatch(listMyCourses());
    dispatch(getCourseDetails({ slug: 'cphq-exam-preparation' }));
  }, [dispatch]);

  const styles = {
    header: { marginBottom: "20px", color: "#555", fontSize: "15px" },
    link: { textDecoration: "none", color: "#007bff" },
  };
  return (
    <div style={styles.header}>
      <span>
        <NavLink to="/" style={styles.link}>
          Home
        </NavLink>
      </span>{" "}
      &gt; <span>Course Details</span>
    </div>
  );
};

const VideoPlayer = ({ chapterId, playbackReady, onEnded }) => {
  const dispatch = useDispatch();
  const videoRef = useRef(null);
  const [playback, setPlayback] = useState(null);
  const [obscured, setObscured] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setPlayback(null);
    setError("");
    if (!chapterId || !playbackReady) return undefined;
    dispatch(getChapterPlayback({ chapterId }))
      .unwrap()
      .then((data) => {
        if (!cancelled) setPlayback(data);
      })
      .catch((apiError) => {
        if (!cancelled) setError(apiError?.error || apiError?.detail || "Video is unavailable.");
      });
    return () => {
      cancelled = true;
    };
  }, [chapterId, dispatch, playbackReady]);

  useEffect(() => {
    const hide = () => {
      setObscured(true);
      const node = videoRef.current;
      if (node && !node.paused) node.pause();
    };
    const show = () => setObscured(false);
    const onVisibility = () => {
      if (document.hidden) hide();
      else show();
    };
    window.addEventListener("blur", hide);
    window.addEventListener("focus", show);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("blur", hide);
      window.removeEventListener("focus", show);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const styles = {
    videoContainer: {
      position: "relative",
      width: "100%",
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
      backgroundColor: "#000",
    },
    video: { width: "100%", display: "block" },
    status: {
      minHeight: "320px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#ffffff",
    },
  };

  if (!chapterId || !playbackReady) {
    return (
      <div style={{ ...styles.videoContainer, ...styles.status }}>
        Select a chapter to play.
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...styles.videoContainer, ...styles.status }}>
        {error}
      </div>
    );
  }

  if (!playback?.url) {
    return (
      <div style={{ ...styles.videoContainer, ...styles.status }}>
        Loading video...
      </div>
    );
  }

  return (
    <div
      className={obscured ? "nexorah-video-obscured" : undefined}
      style={styles.videoContainer}
      onContextMenu={(e) => e.preventDefault()}
    >
      <video
        ref={videoRef}
        key={playback.url}
        controls
        controlsList="nodownload noremoteplayback"
        disablePictureInPicture
        disableRemotePlayback
        playsInline
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        preload="metadata"
        style={styles.video}
        onEnded={onEnded}
        src={playback.url}
      />
    </div>
  );
};

const quizDualRow = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
  flexGrow: 1,
};

const quizSecondaryBtn = {
  background: "#FFFFFF",
  color: "#0D355C",
  border: "1px solid #D0D5DD",
};

const CourseInfo = ({
  about,
  description,
  isMobile,
  activeChapter,
  hasChapterDraft,
  onResumeChapterQuiz,
  onStartNewChapterQuiz,
}) => {
  const baseStyles = {
    container: {
      backgroundColor: "#ffffff",
      padding: "22px",
      marginTop: "-10px",
    },
    buttonContainer: {
      display: "flex",
      gap: "12px",
      marginBottom: "25px",
      alignItems: "center",
    },
    button: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      padding: "10px 16px",
      borderRadius: "8px",
      border: "1px solid #cdd3d9",
      backgroundColor: "#ffffff",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: "600",
      color: "#333",
      transition: "background-color 0.2s ease",
      flexGrow: 1,
    },
    aiButton: {
      backgroundColor: "#0D355C",
      color: "white",
      border: "1px solid #0D355C",
    },
    sectionTitle: {
      fontWeight: "bold",
      fontSize: "16px",
      color: "#0A2540",
      marginBottom: "10px",
    },
    paragraph: {
      fontSize: "14px",
      color: "#555",
      lineHeight: "1.6",
      margin: 0,
    },
    separator: {
      border: 0,
      height: "1px",
      backgroundColor: "#eef2f7",
      margin: "25px 0",
    },
  };

  // Responsive styles that will be merged on mobile
  const mobileStyles = {
    buttonContainer: { flexDirection: "column" },
    button: { width: "100%" },
    aiButton: { marginLeft: 0 },
  };

  const navigate = useNavigate();
  return (
    <div style={baseStyles.container}>
      <div
        style={{
          ...baseStyles.buttonContainer,
          ...(isMobile && mobileStyles.buttonContainer),
        }}
      >
        {chapterHasQuiz(activeChapter) && (
          <div
            style={{
              display: "flex",
              gap: "12px",
              width: isMobile ? "100%" : "auto",
            }}
          >
            {hasChapterDraft ? (
              <div style={{ ...quizDualRow, width: isMobile ? "100%" : "auto" }}>
                <button
                  type="button"
                  style={{ ...baseStyles.button, ...(isMobile ? {} : { flex: 1 }) }}
                  onClick={onResumeChapterQuiz}
                  disabled={!activeChapter?.id}
                >
                  <QuickQuizIcon /> Resume
                </button>
                <button
                  type="button"
                  style={{
                    ...baseStyles.button,
                    ...quizSecondaryBtn,
                    ...(isMobile ? {} : { flex: 1 }),
                  }}
                  onClick={onStartNewChapterQuiz}
                  disabled={!activeChapter?.id}
                >
                  Start new
                </button>
              </div>
            ) : (
              <button
                style={baseStyles.button}
                onClick={() => navigate(`/quick-quiz?chapter_id=${activeChapter?.id || ""}`, { state: { chapterId: activeChapter?.id } })}
                disabled={!activeChapter?.id}
              >
                <QuickQuizIcon />{" "}
                {activeChapter?.quiz_attempted || activeChapter?.quiz_completed ? "Retake Quiz" : "Quick Quiz"}
              </button>
            )}
          </div>
        )}
        <button
          style={{
            ...baseStyles.button,
            ...baseStyles.aiButton,
            marginLeft: isMobile ? 0 : "auto",
          }}
        >
          <AiIcon /> Nexorah AI
        </button>
      </div>
      <div>
        <h3 style={baseStyles.sectionTitle}>About this course</h3>
        <p style={baseStyles.paragraph}>{about}</p>
      </div>
      <hr style={baseStyles.separator} />
      <div>
        <h3 style={baseStyles.sectionTitle}>Description</h3>
        <p style={baseStyles.paragraph}>{description}</p>
      </div>
    </div>
  );
};

const CourseChapters = ({
  domains = [],
  relatedCourses = [],
  selectedChapterId,
  onSelectChapter,
  isMobile,
  hasActiveSubscription,
  quizDrafts = [],
  onResumeModuleMcq,
  onStartNewModuleMcq,
}) => {
  const navigate = useNavigate();
  const [openDomainId, setOpenDomainId] = useState(undefined);
  // Sync dropdown to the playing chapter only when that chapter changes
  // (progress advance or user picks a chapter). Manual module toggles are left alone.
  const lastSyncedChapterRef = useRef(null);

  const chapterAccessById = useMemo(() => {
    return buildChapterAccessMap(domains, hasActiveSubscription);
  }, [domains, hasActiveSubscription]);

  useEffect(() => {
    if (!domains.length) return;

    const activeDomain = domains.find((domain) =>
      (domain.chapters || []).some((chapter) => String(chapter.id) === String(selectedChapterId))
    );

    if (
      selectedChapterId != null &&
      String(lastSyncedChapterRef.current) !== String(selectedChapterId)
    ) {
      lastSyncedChapterRef.current = selectedChapterId;
      if (activeDomain) {
        setOpenDomainId(activeDomain.id);
        return;
      }
    }

    if (openDomainId === undefined) {
      setOpenDomainId(activeDomain?.id ?? domains[0]?.id ?? null);
    }
  }, [domains, openDomainId, selectedChapterId]);

  const styles = {
    container: {
      backgroundColor: "#fff",
      padding: isMobile ? "16px" : "20px",
      borderRadius: "12px",
      marginTop: isMobile ? "20px" : "38px",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      cursor: "pointer",
      fontFamily: "inherit",
      gap: "14px",
      padding: isMobile ? "12px 0" : "14px 0",
    },
    headerTextContainer: { display: "flex", flexDirection: "column", minWidth: 0 },
    title: {
      fontWeight: "600",
      fontSize: isMobile ? "14px" : "15px",
      lineHeight: "1.35",
      color: "#0A2540",
      margin: 0,
    },
    sectionTitle: {
      fontWeight: "700",
      fontSize: isMobile ? "16px" : "17px",
      lineHeight: "1.3",
      color: "#0A2540",
      margin: 0,
    },
    progress: {
      fontSize: isMobile ? "11px" : "12px",
      lineHeight: "1.4",
      color: "#6c757d",
      margin: "4px 0 0 0",
    },
    arrow: {
      fontSize: "16px",
      color: "#6c757d",
      flexShrink: 0,
      transition: "transform 220ms ease",
    },
    chapterList: {
      listStyle: "none",
      padding: 0,
      margin: 0,
      borderTop: "1px solid #f0f0f0",
    },
    moduleItem: {
      borderTop: "1px solid #f0f0f0",
    },
    moduleBody: {
      overflowY: "auto",
      overflowX: "hidden",
      transition: "max-height 260ms ease, opacity 220ms ease",
    },
    chapterItem: {
      display: "flex",
      alignItems: "center",
      width: "100%",
      gap: "12px",
      padding: isMobile ? "10px 8px" : "11px 10px",
      background: "transparent",
      textAlign: "left",
      cursor: "pointer",
      borderRadius: "8px",
      border: "1px solid transparent",
      transition: "background-color 180ms ease, border-color 180ms ease, opacity 180ms ease",
    },
    selectedChapterItem: {
      backgroundColor: "#e8f1ff",
      borderColor: "#0D355C",
    },
    lockedChapterItem: {
      cursor: "not-allowed",
      backgroundColor: "#f7f8fb",
      borderColor: "#e1e6ee",
    },
    chapterInfo: { flexGrow: 1 },
    chapterTitle: {
      margin: 0,
      fontSize: isMobile ? "13px" : "14px",
      lineHeight: "1.4",
      color: "#263238",
      fontWeight: 400,
    },
    selectedChapterTitle: {
      color: "#0D355C",
      fontWeight: 700,
    },
    lockedChapterTitle: {
      color: "#7b8794",
    },
    chapterDuration: {
      fontSize: "11px",
      lineHeight: "1.35",
      color: "#7b8794",
      marginTop: "4px",
    },
    relatedCourse: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      padding: isMobile ? "16px 0" : "18px 0",
      borderTop: "1px solid #f0f0f0",
      borderLeft: 0,
      borderRight: 0,
      borderBottom: 0,
      background: "transparent",
      color: "inherit",
      cursor: "pointer",
      textAlign: "left",
    },
    relatedCourseArrow: { fontSize: "20px", color: "#333", flexShrink: 0 },
    lockBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      color: "#8a4b08",
      backgroundColor: "#fff4df",
      border: "1px solid #ffd89a",
      borderRadius: "999px",
      padding: "5px 8px",
      fontSize: "11px",
      lineHeight: 1,
      fontWeight: 700,
      flexShrink: 0,
    },
    progressLockBadge: {
      color: "#40566d",
      backgroundColor: "#eaf2fb",
      borderColor: "#c9def4",
    },
    moduleLockBadge: {
      marginLeft: "auto",
    },
  };

  return (
    <div style={styles.container}>
      <div style={{ ...styles.header, cursor: "default", paddingTop: 0 }}>
        <div style={styles.headerTextContainer}>
          <h3 style={styles.sectionTitle}>Course Modules</h3>
          <p style={styles.progress}>{`${domains.length} modules`}</p>
        </div>
      </div>

      <div style={styles.chapterList}>
        {domains.map((domain) => {
          const chapters = domain.chapters || [];
          const completedCount = chapters.filter((chapter) => isChapterCompleted(chapter)).length;
          const isOpen = String(openDomainId) === String(domain.id);
          const domainLocked = chapters.length > 0 && chapters.every((chapter) => chapterAccessById.get(String(chapter.id))?.isLocked);
          const domainLockType = chapters.find((chapter) => chapterAccessById.get(String(chapter.id))?.lockType)?.id;
          const domainLock = domainLockType ? chapterAccessById.get(String(domainLockType)) : null;

          return (
            <div key={domain.id} style={styles.moduleItem}>
              <button
                type="button"
                style={{ ...styles.header, width: "100%", border: 0, background: "transparent", textAlign: "left", fontFamily: "inherit" }}
                onClick={() => setOpenDomainId(isOpen ? null : domain.id)}
                aria-expanded={isOpen}
              >
                <div style={styles.headerTextContainer}>
                  <h3 style={styles.title}>{domain.title}</h3>
                  <p style={styles.progress}>{`${completedCount} / ${chapters.length} chapters`}</p>
                </div>
                {domainLocked && (
                  <span
                    style={{
                      ...styles.lockBadge,
                      ...styles.moduleLockBadge,
                      ...(domainLock?.lockType === "progress" ? styles.progressLockBadge : {}),
                    }}
                    title={domainLock?.lockType === "subscription" ? "Subscription required" : "Finish the previous chapter quiz first"}
                  >
                    {domainLock?.lockType === "subscription" ? <SubscriptionLockIcon /> : <ProgressLockIcon />}
                    {domainLock?.lockType === "subscription" ? "Locked" : "Next"}
                  </span>
                )}
                <span style={{ ...styles.arrow, transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>&#9660;</span>
              </button>

              <div
                style={{
                  ...styles.moduleBody,
                  maxHeight: isOpen ? "720px" : 0,
                  opacity: isOpen ? 1 : 0,
                }}
              >
                <ul style={{ ...styles.chapterList, borderTop: 0, paddingBottom: isOpen ? "8px" : 0 }}>
                  <li>
                    {quizDrafts.some((draft) => draft.quiz_type === 'module_assessment' && Number(draft.target_id) === Number(domain.id)) ? (
                      <div style={{ display: "flex", gap: "8px", padding: "10px 0", flexWrap: "wrap" }}>
                        <button
                          type="button"
                          style={{
                            ...styles.chapterItem,
                            fontWeight: 700,
                            color: domainLocked ? "#98A2B3" : "#0D355C",
                            flex: 1,
                            minWidth: "120px",
                          }}
                          disabled={domainLocked}
                          onClick={() => {
                            if (domainLocked) return;
                            onResumeModuleMcq(domain.id);
                          }}
                        >
                          Resume Module MCQ
                        </button>
                        <button
                          type="button"
                          style={{
                            ...styles.chapterItem,
                            fontWeight: 600,
                            color: domainLocked ? "#98A2B3" : "#0D355C",
                            flex: 1,
                            minWidth: "120px",
                            background: "#fff",
                            border: "1px solid #D0D5DD",
                          }}
                          disabled={domainLocked}
                          onClick={() => {
                            if (domainLocked) return;
                            onStartNewModuleMcq(domain.id);
                          }}
                        >
                          Start new
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        style={{
                          ...styles.chapterItem,
                          fontWeight: 700,
                          color: domainLocked ? "#98A2B3" : "#0D355C",
                        }}
                        disabled={domainLocked}
                        onClick={() => {
                          if (domainLocked) return;
                          navigate(`/quick-quiz?domain_id=${domain.id}`, { state: { domainId: domain.id } });
                        }}
                      >
                        Module MCQ
                      </button>
                    )}
                  </li>
                  {chapters.map((chapter) => {
                    const chapterAccess = chapterAccessById.get(String(chapter.id));
                    const isLocked = chapterAccess?.isLocked;
                    const lockType = chapterAccess?.lockType;
                    const isCurrent = String(selectedChapterId) === String(chapter.id);

                    return (
                      <li key={chapter.id}>
                        <button
                          type="button"
                          style={{
                            ...styles.chapterItem,
                            ...(isCurrent ? styles.selectedChapterItem : {}),
                            ...(isLocked ? styles.lockedChapterItem : {}),
                          }}
                          onClick={() => {
                            if (isLocked) return;
                            onSelectChapter(chapter);
                          }}
                          title={isLocked ? (lockType === "subscription" ? "Subscription required" : "Finish the previous chapter quiz first") : chapter.title}
                        >
                          <CustomCheckbox completed={isChapterCompleted(chapter)} />
                          <div style={styles.chapterInfo}>
                            <p
                              style={{
                                ...styles.chapterTitle,
                                ...(isCurrent ? styles.selectedChapterTitle : {}),
                                ...(isLocked ? styles.lockedChapterTitle : {}),
                              }}
                            >
                              {chapter.title}
                            </p>
                            <div style={styles.chapterDuration}>{chapter.duration_minutes} min</div>
                          </div>
                          {isLocked && (
                            <span
                              style={{
                                ...styles.lockBadge,
                                ...(lockType === "progress" ? styles.progressLockBadge : {}),
                              }}
                            >
                              {lockType === "subscription" ? <SubscriptionLockIcon /> : <ProgressLockIcon />}
                              {lockType === "subscription" ? "Locked" : "Next"}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
      {relatedCourses.map((course, index) => (
        <button type="button" key={index} style={{ ...styles.relatedCourse, fontFamily: "inherit" }}>
          <div style={styles.headerTextContainer}>
            <h3 style={styles.title}>{course.title}</h3>
            <p style={styles.progress}>{course.progress}</p>
          </div>
          <span style={styles.relatedCourseArrow}>&rarr;</span>
        </button>
      ))}
    </div>
  );
};

const getMessageText = (message) =>
  message?.text ||
  message?.message ||
  message?.content ||
  message?.response ||
  message?.answer ||
  "";

const isAiMessage = (message) => {
  const sender = message?.from || message?.sender || message?.role;
  return ["gpt", "ai", "assistant", "tutor"].includes(String(sender).toLowerCase());
};

const Chatbox = ({ courseId }) => {
  const dispatch = useDispatch();
  const { chatHistory = [], loading } = useSelector((state) => state.course);
  const [input, setInput] = useState("");

  useEffect(() => {
    if (!courseId) return;

        dispatch(getCourseChatHistory({ courseId }));
  }, [courseId, dispatch]);

  const handleSend = async () => {
    const message = input.trim();
    if (!message || !courseId) return;

    try {
      await dispatch(sendMessageToAiTutor({ courseId, message })).unwrap();
      setInput("");
      dispatch(getCourseChatHistory({ courseId }));
    } catch (err) {
          }
  };

  const styles = {
    chatContainer: {
      marginTop: "20px",
      backgroundColor: "#fff",
      padding: "22px",
      borderRadius: "12px",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: "15px",
      alignItems: "center",
    },
    headerTitle: {
      margin: 0,
      fontSize: "16px",
      fontWeight: "600",
      color: "#0A2540",
    },
    seeAllLink: {
      color: "#007bff",
      fontSize: "13px",
      background: "transparent",
      border: 0,
      cursor: "pointer",
      padding: 0,
    },
    messagesContainer: {
      height: "250px",
      overflowY: "auto",
      marginBottom: "15px",
      paddingRight: "10px",
    },
    message: {
      marginBottom: "10px",
      padding: "12px",
      borderRadius: "10px",
      maxWidth: "85%",
      lineHeight: "1.5",
      fontSize: "14px",
    },
    gptMessage: { backgroundColor: "#f1f1f1", alignSelf: "flex-start" },
    userMessage: {
      backgroundColor: "#0A2540",
      color: "white",
      marginLeft: "auto",
    },
    inputContainer: { display: "flex", gap: "10px" },
    input: {
      flexGrow: 1,
      padding: "12px 18px",
      borderRadius: "25px",
      border: "1px solid #ddd",
    },
    sendButton: {
      padding: "12px 25px",
      borderRadius: "25px",
      border: "none",
      backgroundColor: "#2979FF",
      color: "white",
      cursor: "pointer",
    },

    
  };

  return (
    <div style={styles.chatContainer}>
      <div style={styles.header}>
        <h3 style={styles.headerTitle}>Previous conversation</h3>
        <button type="button" style={styles.seeAllLink}>
          See All
        </button>
      </div>
      <div style={styles.messagesContainer}>
        {chatHistory.length === 0 ? (
          <p>No messages yet...</p>
        ) : (
          chatHistory.map((msg, index) => (
            <div
              key={msg.id || index}
              style={{
                ...styles.message,
                ...(isAiMessage(msg) ? styles.gptMessage : styles.userMessage),
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>

                {getMessageText(msg)}
              </ReactMarkdown>
            </div>

          ))
        )}
      </div>
      <div style={styles.inputContainer}>
        <input
          type="text"
          style={styles.input}
          placeholder="Message to AI tutor..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
        />
        <button style={styles.sendButton} onClick={handleSend} disabled={loading || !input.trim()}>
          {loading ? "Sending..." : "Send"}
        </button>
      </div>
    </div>
  );
};

// --- MAIN PAGE COMPONENT ---

const CourseDetails = () => {
  // --- State & Data ---
  const { isTablet: isMobile } = useResponsive();
  const [selectedChapter, setSelectedChapter] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // --- Styles ---
  const baseStyles = {
    container: {
      display: "flex",
      fontFamily:
        'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      backgroundColor: "#ECF2FE", // Overall light blue background
      padding: "22px",

      color: "#333",
      gap: "20px", // Use gap for consistent spacing
    },
    mainContent: {
      flex: 3, // Takes 3/4 of the space
      minWidth: 0, // Prevents flexbox overflow
    },
    sidebar: {
      flex: 1, // Takes 1/4 of the space
      minWidth: "300px", // Prevents sidebar from getting too squished
    },
    // New style for the white background header
    whiteHeaderContainer: {
      backgroundColor: "#ffffff",
      padding: "25px",
      borderRadius: "12px",

      // Space below the header
    },
    courseTitle: {
      fontSize: "28px",
      fontWeight: "700px",
      color: "#0F141F",
      margin: "0 0 5px 0",
    },
    courseProgress: {
      fontWeight: "400px",
      fontSize: "14px",
      color: "#0C1018",
      margin: "0",
      marginBottom: "10px",
    },
  };

  // Responsive styles that will be merged on mobile
  const mobileStyles = {
    container: {
      flexDirection: "column",
      padding: "15px",
    },
    sidebar: {
      marginTop: "20px",
      minWidth: "auto", // Allow sidebar to be full width
    },
    // Adjust padding for mobile header
    whiteHeaderContainer: {
      padding: "20px",
    },
  };


  const { courseDetailsData, quizDrafts = [] } = useSelector((state) => state.course);
  const { subscriptionStatus } = useSelector((state) => state.payment);
  const hasActiveSubscription = Boolean(subscriptionStatus?.is_active || subscriptionStatus?.status === "active");
  const domains = useMemo(() => courseDetailsData?.domains || [], [courseDetailsData]);
  const chapters = useMemo(() => getOrderedChapters(domains), [domains]);
  const chapterAccessById = useMemo(
    () => buildChapterAccessMap(domains, hasActiveSubscription),
    [domains, hasActiveSubscription]
  );

  useEffect(() => {
    dispatch(getSubscriptionStatus());
    dispatch(getQuizDrafts());
  }, [dispatch]);

  useEffect(() => {
    if (!chapters.length) return;

    const selectedAccess = selectedChapter ? chapterAccessById.get(String(selectedChapter.id)) : null;
    const selectedStillExists = chapters.some((chapter) => String(chapter.id) === String(selectedChapter?.id));
    const progressChapter = getProgressChapter(chapters, chapterAccessById);
    const resumeAfterQuiz = Boolean(location.state?.resumeAfterQuiz);
    // Opened from Weakest Areas: show that exact chapter (video + quiz button).
    const openChapterId = location.state?.openChapterId;
    if (openChapterId != null) {
      const target = chapters.find((chapter) => String(chapter.id) === String(openChapterId));
      navigate(location.pathname, { replace: true, state: null });
      if (target && !chapterAccessById.get(String(target.id))?.isLocked) {
        setSelectedChapter(target);
        return;
      }
    }
    // After a passed quiz, open the next incomplete unlocked chapter (e.g. Chapter 2).
    if (resumeAfterQuiz && progressChapter) {
      setSelectedChapter(progressChapter);
      navigate(location.pathname, { replace: true, state: null });
      return;
    }

    // On first load / refresh (no selection), or if selection is gone/locked, resume by progress.
    if (!selectedChapter || !selectedStillExists || selectedAccess?.isLocked) {
      setSelectedChapter(progressChapter);
    }
  }, [chapterAccessById, chapters, selectedChapter, location.state, location.pathname, navigate]);

  const selectedActiveChapter = chapters.find((chapter) => String(chapter.id) === String(selectedChapter?.id));
  const fallbackChapter = getProgressChapter(chapters, chapterAccessById);
  const activeChapter = selectedActiveChapter && !chapterAccessById.get(String(selectedActiveChapter.id))?.isLocked
    ? selectedActiveChapter
    : fallbackChapter;

  const handleVideoEnded = async () => {
    if (!activeChapter?.id) return;

    const alreadyWatched = isVideoWatched(activeChapter);
    if (!alreadyWatched) {
      await dispatch(markChapterProgress({ chapterId: activeChapter.id, completed: true }));
    }

    // Quiz chapters: after the video ends, auto-open quiz until the learner passes.
    if (chapterHasQuiz(activeChapter)) {
      if (!activeChapter.quiz_completed) {
        navigate(`/quick-quiz?chapter_id=${activeChapter.id}`, { state: { chapterId: activeChapter.id } });
        return;
      }
      // Watched + already passed: reviewing only — do not jump away on rewatch.
      return;
    }

    // Intro / no-quiz: advance only the first time the video is completed.
    if (alreadyWatched) {
      return;
    }
    const currentIdx = chapters.findIndex((chapter) => String(chapter.id) === String(activeChapter.id));
    const nextChapter = currentIdx >= 0 ? chapters[currentIdx + 1] : null;
    if (nextChapter) {
      setSelectedChapter(nextChapter);
    }
  };

  const openChapterQuiz = (chapterId, { startNew = false } = {}) => {
    if (!chapterId) return;
    navigate(`/quick-quiz?chapter_id=${chapterId}`, {
      state: { chapterId, startNew },
    });
  };

  const handleResumeChapterQuiz = () => {
    openChapterQuiz(activeChapter?.id, { startNew: false });
  };

  const handleStartNewChapterQuiz = async () => {
    if (!activeChapter?.id) return;
    await dispatch(clearQuizDraft({ quizType: 'chapter_quiz', targetId: activeChapter.id }));
    dispatch(getQuizDrafts());
    openChapterQuiz(activeChapter.id, { startNew: true });
  };

  const handleResumeModuleMcq = (domainId) => {
    navigate(`/quick-quiz?domain_id=${domainId}`, { state: { domainId, startNew: false } });
  };

  const handleStartNewModuleMcq = async (domainId) => {
    await dispatch(clearQuizDraft({ quizType: 'module_assessment', targetId: domainId }));
    dispatch(getQuizDrafts());
    navigate(`/quick-quiz?domain_id=${domainId}`, { state: { domainId, startNew: true } });
  };

  return (
    <div
      style={{
        ...baseStyles.container,
        ...(isMobile && mobileStyles.container),
      }}
    >
      <div style={baseStyles.mainContent}>
        <Header />
        {/* NEW: White background for course title and progress */}
        <div
          style={{
            ...baseStyles.whiteHeaderContainer,
            ...(isMobile && mobileStyles.whiteHeaderContainer),

          }}
        >

          <h1 style={baseStyles.courseTitle}>{courseDetailsData?.title}</h1>
          <p style={baseStyles.courseProgress}>{courseDetailsData?.progress}</p>
          <VideoPlayer
            chapterId={activeChapter?.id}
            playbackReady={Boolean(activeChapter?.playback_ready)}
            onEnded={handleVideoEnded}
          />
        </div>

        <CourseInfo
          about={courseDetailsData?.about}
          description={courseDetailsData?.description}
          isMobile={isMobile}
          activeChapter={activeChapter}
          hasChapterDraft={quizDrafts.some((draft) => draft.quiz_type === 'chapter_quiz' && Number(draft.target_id) === Number(activeChapter?.id))}
          onResumeChapterQuiz={handleResumeChapterQuiz}
          onStartNewChapterQuiz={handleStartNewChapterQuiz}
        />
        {courseDetailsData && (
          <Chatbox courseId={courseDetailsData.id} />
        )}
      </div>
      <div
        style={{ ...baseStyles.sidebar, ...(isMobile && mobileStyles.sidebar) }}
      >
        <CourseChapters
          domains={domains}
          relatedCourses={courseDetailsData?.relatedCourses || []}
          selectedChapterId={activeChapter?.id}
          onSelectChapter={setSelectedChapter}
          isMobile={isMobile}
          hasActiveSubscription={hasActiveSubscription}
          quizDrafts={quizDrafts}
          onResumeModuleMcq={handleResumeModuleMcq}
          onStartNewModuleMcq={handleStartNewModuleMcq}
        />
      </div>
    </div>
  );
};

export default CourseDetails;
